import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X } from 'lucide-react';

const empty = {
  name: '', code: '', description: '', indications: '', dosage: '', price: '', notes: '',
};

export default function Supplements() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await supabase.from('supplements').select('*').order('name');
    setItems(data || []);
  };

  const filtered = items.filter(s =>
    `${s.name} ${s.indications} ${s.code}`.toLowerCase().includes(search.toLowerCase())
  );

  const saveNew = async () => {
    if (!form.name) return;
    await supabase.from('supplements').insert([form]);
    setForm(empty); setShowNew(false); load();
  };

  const saveEdit = async () => {
    const { id, created_at, ...rest } = editing;
    await supabase.from('supplements').update(rest).eq('id', id);
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this supplement?')) return;
    await supabase.from('supplements').delete().eq('id', id);
    setExpanded(null); load();
  };

  const Form = ({ data, setData, onSave, onCancel }) => (
    <div className="patient-form">
      <div className="form-grid">
        <div className="form-field"><label>Product Name *</label><input value={data.name} onChange={e => setData({ ...data, name: e.target.value })} /></div>
        <div className="form-field"><label>Product Code</label><input value={data.code} onChange={e => setData({ ...data, code: e.target.value })} placeholder="CYT-001" /></div>
        <div className="form-field"><label>Price (£)</label><input type="number" step="0.01" value={data.price} onChange={e => setData({ ...data, price: e.target.value })} /></div>
        <div className="form-field"><label>Suggested Dosage</label><input value={data.dosage} onChange={e => setData({ ...data, dosage: e.target.value })} placeholder="1 capsule daily with food" /></div>
        <div className="form-field full"><label>Description</label><textarea rows={2} value={data.description} onChange={e => setData({ ...data, description: e.target.value })} /></div>
        <div className="form-field full"><label>Key Indications</label><textarea rows={2} value={data.indications} onChange={e => setData({ ...data, indications: e.target.value })} placeholder="Gut health, immunity, inflammation..." /></div>
        <div className="form-field full"><label>Notes</label><textarea rows={2} value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} /></div>
      </div>
      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn-gold" onClick={onSave}><Save size={14} /> Save</button>
      </div>
    </div>
  );

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Cytoplan Supplements</h2>
          <p className="section-sub">{items.length} supplement{items.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-gold" onClick={() => { setShowNew(true); setExpanded(null); }}>
          <Plus size={14} /> Add Supplement
        </button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search supplements..." className="search-input" />
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <div className="patient-name-block">
              <div className="patient-initials">+</div>
              <div><div className="patient-name">New Supplement</div></div>
            </div>
          </div>
          <Form data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(empty); }} />
        </div>
      )}

      <div className="patient-list">
        {filtered.map(s => (
          <div key={s.id} className={`patient-card ${expanded === s.id ? 'expanded' : ''}`}>
            <div className="card-header" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
              <div className="patient-name-block">
                <div className="patient-initials" style={{ background: 'rgba(58,125,68,0.15)', color: '#3a7d44', fontSize: '11px' }}>CYT</div>
                <div>
                  <div className="patient-name">{s.name} {s.code && <span className="mini-tag">{s.code}</span>}</div>
                  <div className="patient-meta">
                    {s.price && <span>£{s.price}</span>}
                    {s.indications && <span>{s.indications.slice(0, 60)}{s.indications.length > 60 ? '…' : ''}</span>}
                  </div>
                </div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="icon-btn" onClick={() => { setEditing(s); setExpanded(s.id); }}><Edit3 size={14} /></button>
                <button className="icon-btn danger" onClick={() => del(s.id)}><Trash2 size={14} /></button>
                <span className="expand-icon">{expanded === s.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
              </div>
            </div>
            {expanded === s.id && (
              <div className="card-body">
                {editing?.id === s.id ? (
                  <Form data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                ) : (
                  <div className="detail-grid">
                    <div className="detail-row full"><span className="detail-label">Description</span><span>{s.description || '—'}</span></div>
                    <div className="detail-row full"><span className="detail-label">Indications</span><span>{s.indications || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Dosage</span><span>{s.dosage || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Price</span><span>{s.price ? `£${s.price}` : '—'}</span></div>
                    {s.notes && <div className="detail-row full"><span className="detail-label">Notes</span><span>{s.notes}</span></div>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && !showNew && (
          <div className="empty-state">
            <p>No supplements yet</p>
            <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add your first supplement</button>
          </div>
        )}
      </div>
    </div>
  );
}
